import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Card, SectionHeader } from '@/components/Card';
import { AdSpendCard } from '@/components/facebook/AdSpendCard';
import { AdHealthCard } from '@/components/facebook/AdHealthCard';
import { AccountHealthBanner } from '@/components/facebook/AccountHealthBanner';
import { MetaScopeBanner } from '@/components/facebook/MetaScopeBanner';
import { SpendCapPanel } from '@/components/facebook/SpendCapPanel';
import { facebookAdAccountService, MetaAdAccount, AdAccountHealth, WhatsAppOption, WhatsAppPhone } from '@/services/facebook-ad-account.service';
import { FbPagesMultiPicker, IgAccountPicker } from '@/components/facebook/MetaResourcePickers';
import { metaResourcesService, MetaPage, MetaInstagramAccount } from '@/services/meta-resources.service';
import { Link, Camera, Globe, ExternalLink, Image as ImageIcon, Info, MessageCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import type { AxiosError } from 'axios';
import { EmptyState } from '@/components/EmptyState';
import { isNotConnectedError } from '@/config/api';

type Props = {
  /** Rendered inside FacebookAccountsManager's tab shell — suppress the page Topbar. */
  embedded?: boolean;
};

export default function FacebookAdAccountSettings({ embedded = false }: Props = {}) {
  const [acc, setAcc] = useState<MetaAdAccount | null>(null);
  const [health, setHealth] = useState<AdAccountHealth | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [pages, setPages] = useState<MetaPage[] | null>(null);
  const [igAccounts, setIgAccounts] = useState<MetaInstagramAccount[] | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'none' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    setLoadState('loading');
    // The rest only makes sense once there is an account — asking first avoids a burst of failing calls.
    facebookAdAccountService.get().then((a) => {
      setAcc(a);
      facebookAdAccountService.health().then(setHealth).catch(() => undefined);
      metaResourcesService.pages().then(setPages).catch(() => undefined);
      metaResourcesService.instagramAccounts().then(setIgAccounts).catch(() => undefined);
    }).catch((e: AxiosError<{ code?: string; message?: string }>) => {
      setLoadState(isNotConnectedError(e?.response?.status, e?.response?.data) ? 'none' : 'error');
    });
  }, [attempt]);

  // Lookup tables so we can render avatar + name from a stored ID alone.
  const pageById = useMemo(
    () => new Map((pages ?? []).map(p => [p.id, p])),
    [pages],
  );
  const igById = useMemo(
    () => new Map((igAccounts ?? []).map(a => [a.id, a])),
    [igAccounts],
  );

  const save = async () => {
    if (!acc) return;
    setBusy(true);
    try {
      // Keep the legacy `pageId` in sync with the multi-select for backwards
      // compatibility with code paths that still read the singular field.
      const pageIds = acc.pageIds ?? (acc.pageId ? [acc.pageId] : []);
      const defaultPageId = pageIds.includes(acc.pageId ?? '') ? acc.pageId : pageIds[0];
      const updated = await facebookAdAccountService.update({
        adAccountName: acc.adAccountName,
        businessManagerId: acc.businessManagerId,
        pageId: defaultPageId,
        pageIds,
        instagramAccountId: acc.instagramAccountId,
      });
      setAcc(updated);
      toast.success('Saved');
      setEditingProfile(false);
    } finally { setBusy(false); }
  };

  if (!acc && loadState === 'none') {
    return (
      <>
        {!embedded && <Topbar title="Facebook Ad Account" breadcrumb="Facebook Ads" />}
        <div className="px-7 py-7 w-full">
          <EmptyState
            icon={<Globe size={28} />}
            title="No ad account connected yet"
            description="Connect a Meta ad account on the Accounts tab. Its settings — Pages, Instagram, WhatsApp and spend limits — show up here."
            action={<Button iconLeft={<Globe size={15} />} onClick={() => navigate('/facebook/accounts')}>Go to Accounts</Button>}
          />
        </div>
      </>
    );
  }

  if (!acc && loadState === 'error') {
    return (
      <>
        {!embedded && <Topbar title="Facebook Ad Account" breadcrumb="Facebook Ads" />}
        <div className="px-7 py-7 w-full">
          <EmptyState
            icon={<AlertTriangle size={28} />}
            title="Account settings could not be loaded"
            description="Check your connection and try again. If it keeps happening, reconnect the ad account from the Accounts tab."
            action={<Button variant="secondary" iconLeft={<RefreshCw size={15} />} onClick={() => setAttempt((n) => n + 1)}>Retry</Button>}
          />
        </div>
      </>
    );
  }

  if (!acc) {
    return (
      <>
        {!embedded && <Topbar title="Facebook Ad Account" breadcrumb="Facebook Ads" />}
        <div className="px-7 py-7 w-full space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="skeleton h-28 rounded-2xl" />
            <div className="skeleton h-28 rounded-2xl" />
          </div>
          <div className="skeleton h-48 rounded-2xl" />
        </div>
      </>
    );
  }

  return (
    <>
      {!embedded && (
        <Topbar
          title="Facebook Ad Account"
          breadcrumb="Facebook Ads"
          description="Manage your Meta Ads account identity and connected profiles."
        />
      )}

      <div className={clsx('w-full space-y-4 px-7', embedded ? 'pb-24 pt-4' : 'py-7')}>
        <MetaScopeBanner />
        <AccountHealthBanner account={acc} />

        {/* Health KPI row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <AdSpendCard health={health} />
          <AdHealthCard initial={health} onRefresh={setHealth} />
        </div>

        {/* Spend cap & balance */}
        <SpendCapPanel
          health={health}
          account={acc}
          onAccountUpdate={(a) => setAcc(a)}
          onHealthRefresh={setHealth}
        />

        {/* Ad Account identity */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-display text-base font-semibold text-text-primary tracking-tight">Ad Account</div>
              <div className="mt-0.5 font-mono text-xs text-text-muted">{acc.adAccountId}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <InfoRow label="Name" value={<strong>{acc.adAccountName}</strong>} />
            <InfoRow label="Currency" value={<strong>{acc.currency}</strong>} />
            <InfoRow label="Owner" value={acc.facebookUserName ?? '—'} />
            <InfoRow label="FB user ID" value={<span className="font-mono text-xs">{acc.facebookUserId ?? '—'}</span>} />
            <InfoRow label="App ID" value={<span className="font-mono text-xs">{acc.appId}</span>} />
            <InfoRow label="API" value={<span className="font-mono text-xs">{acc.apiBaseUrl}/{acc.apiVersion}</span>} />
          </div>
        </Card>

        {/* Connected profiles */}
        <Card>
          <SectionHeader
            title="Connected profiles"
            className="mb-4"
            action={
              <Button
                size="sm"
                variant={editingProfile ? 'primary' : 'secondary'}
                onClick={() => editingProfile ? save() : setEditingProfile(true)}
                loading={busy}
              >
                {editingProfile ? 'Save' : 'Edit'}
              </Button>
            }
          />

          {/* Grid layout — Pages + Instagram side-by-side, Business Manager
              spans both columns since its info pane is wider when editing.
              Collapses to a single column on small screens. */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <BusinessManagerRow
                editing={editingProfile}
                value={acc.businessManagerId}
                onChange={(v) => setAcc({ ...acc, businessManagerId: v })}
              />
            </div>
            <PagesRow
              editing={editingProfile}
              pageIds={acc.pageIds ?? (acc.pageId ? [acc.pageId] : [])}
              pageById={pageById}
              onChange={(ids) => setAcc({ ...acc, pageIds: ids })}
            />
            <InstagramRow
              editing={editingProfile}
              value={acc.instagramAccountId ?? null}
              igById={igById}
              onChange={(id) => setAcc({ ...acc, instagramAccountId: id ?? undefined })}
            />
          </div>

          {editingProfile && (
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-border">
              <Button variant="secondary" onClick={() => setEditingProfile(false)}>Cancel</Button>
              <Button disabled={busy} onClick={save}>Save changes</Button>
            </div>
          )}
        </Card>

        {/* WhatsApp Business Account */}
        <WhatsAppSection account={acc} onAccountUpdate={setAcc} />

        <div className="text-xs text-text-muted">
          <a
            href="https://business.facebook.com/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            Open Meta Business Manager <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </>
  );
}

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <span className="text-text-muted">{label}: </span>
    {value}
  </div>
);

const WhatsAppSection: React.FC<{ account: MetaAdAccount; onAccountUpdate: (a: MetaAdAccount) => void }> = ({ account, onAccountUpdate }) => {
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState<WhatsAppOption[] | null>(null);
  const [selectedKey, setSelectedKey] = useState<string>('');

  const connected = !!account.whatsappBusinessAccountId;
  const verifyExpired = account.whatsappCodeVerificationStatus === 'EXPIRED';
  const qualityLow = account.whatsappQualityRating === 'RED' || account.whatsappQualityRating === 'YELLOW';

  const probe = async () => {
    setBusy(true);
    try {
      const r = await facebookAdAccountService.probeWhatsApp(account.accountUid);
      if (r.requiresChoice) {
        setPicker(r.available);
      } else {
        onAccountUpdate(r.account);
        if (r.selected) toast.success(`Connected ${r.selected.phone.displayPhoneNumber}`);
        else toast('No WhatsApp Business Account found on this user', { icon: '⚠️' });
      }
    } catch { /* toast shown by interceptor */ }
    finally { setBusy(false); }
  };

  const commit = async () => {
    if (!selectedKey) return;
    const [wabaId, phoneNumberId] = selectedKey.split('|');
    setBusy(true);
    try {
      const r = await facebookAdAccountService.probeWhatsApp(account.accountUid, { wabaId, phoneNumberId });
      if (!r.requiresChoice) {
        onAccountUpdate(r.account);
        setPicker(null);
        setSelectedKey('');
        toast.success(`Connected ${r.selected?.phone.displayPhoneNumber ?? phoneNumberId}`);
      }
    } catch { /* toast shown by interceptor */ }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect WhatsApp from this ad account? CTWA ads will stop working until you reconnect.')) return;
    setBusy(true);
    try {
      const a = await facebookAdAccountService.clearWhatsApp(account.accountUid);
      onAccountUpdate(a);
      toast.success('WhatsApp disconnected');
    } catch { /* toast shown by interceptor */ }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <SectionHeader
        title="WhatsApp Business"
        subtitle="Required for Click-to-WhatsApp ads + Cloud API messaging"
        className="mb-4"
        action={
          connected ? (
            <Button size="sm" variant="secondary" onClick={disconnect} loading={busy}>Disconnect</Button>
          ) : (
            <Button size="sm" onClick={probe} loading={busy}>Connect WhatsApp</Button>
          )
        }
      />

      <div className="flex items-start gap-3 rounded-xl border border-border bg-surface p-3.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-success-soft text-success">
          <MessageCircle size={16} />
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          {connected ? (
            <>
              <div className="flex items-center gap-2">
                <strong className="text-sm text-text-primary">{account.whatsappVerifiedName ?? 'WhatsApp'}</strong>
                <span className="font-mono text-xs text-text-muted">{account.whatsappDisplayPhoneNumber}</span>
              </div>
              <div className="grid grid-cols-2 gap-1.5 text-xs">
                <div className="text-text-muted">WABA: <span className="font-mono text-text-primary">{account.whatsappBusinessAccountId}</span></div>
                <div className="text-text-muted">Phone ID: <span className="font-mono text-text-primary">{account.whatsappPhoneNumberId}</span></div>
                <div className="text-text-muted">Quality: <span className={qualityLow ? 'font-semibold text-warn' : 'font-semibold text-success'}>{account.whatsappQualityRating ?? '—'}</span></div>
                <div className="text-text-muted">Verification: <span className={verifyExpired ? 'font-semibold text-danger' : 'font-semibold text-success'}>{account.whatsappCodeVerificationStatus ?? '—'}</span></div>
              </div>
              {verifyExpired && (
                <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  <div>
                    Number verification has <strong>expired</strong>. Cloud API messaging will fail until you re-verify
                    in <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="underline">Meta Developers → WhatsApp → Phone numbers</a>.
                    Ads will still serve.
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-xs text-text-muted italic">
              Not connected. Click "Connect WhatsApp" to discover the Business Accounts reachable from this ad account.
            </div>
          )}
        </div>
        {connected && (
          <span className="inline-flex items-center rounded-full bg-success-soft px-2 py-0.5 text-xs font-semibold text-success shrink-0">
            <span className="mr-1 h-1.5 w-1.5 rounded-full bg-success" /> Connected
          </span>
        )}
      </div>

      {picker && (
        <div className="mt-4 space-y-2">
          <div className="text-xs text-text-secondary">
            Found {picker.length} WhatsApp Business Account{picker.length === 1 ? '' : 's'}. Pick the phone number to use for ads + messaging:
          </div>
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {picker.flatMap(w => w.phones.map(p => {
              const key = `${w.wabaId}|${p.id}`;
              return (
                <label key={key} className={`flex items-start gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${selectedKey === key ? 'border-primary bg-primary-soft' : 'border-border hover:bg-surface-alt'}`}>
                  <input
                    type="radio"
                    name="waba-phone"
                    value={key}
                    checked={selectedKey === key}
                    onChange={(e) => setSelectedKey(e.target.value)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <strong className="text-sm text-text-primary">{p.verifiedName ?? p.displayPhoneNumber}</strong>
                      <span className="text-xs text-text-muted">{p.displayPhoneNumber}</span>
                    </div>
                    <div className="text-2xs text-text-muted">
                      WABA: <span className="font-mono">{w.wabaName ?? w.wabaId}</span>
                      {w.businessName && <> · Business: {w.businessName}</>}
                      {' '}· quality: {p.qualityRating ?? '—'}
                      {p.codeVerificationStatus === 'EXPIRED' && <span className="text-danger"> · verification expired</span>}
                    </div>
                  </div>
                </label>
              );
            }))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" size="sm" onClick={() => { setPicker(null); setSelectedKey(''); }}>Cancel</Button>
            <Button size="sm" disabled={!selectedKey || busy} onClick={commit} loading={busy}>Connect selected</Button>
          </div>
        </div>
      )}
    </Card>
  );
};

const PagesRow: React.FC<{
  editing: boolean;
  pageIds: string[];
  pageById: Map<string, MetaPage>;
  onChange: (ids: string[]) => void;
}> = ({ editing, pageIds, pageById, onChange }) => (
  <div className={`flex items-start gap-3 rounded-xl border p-3.5 transition-colors ${editing ? 'border-border-strong bg-surface-alt' : 'border-border bg-surface'}`}>
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
      <Link size={16} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-xs font-medium text-text-secondary mb-1">Facebook Pages</div>
      {editing ? (
        <>
          <div className="text-2xs text-text-muted mb-2">
            Pick the Pages your team can use as the actor for ads. The selected Page on each ad set's
            "Where ads will run" must come from this list.
          </div>
          <FbPagesMultiPicker value={pageIds} onChange={onChange} />
        </>
      ) : pageIds.length === 0 ? (
        <span className="italic text-text-muted text-xs">Not connected</span>
      ) : (
        <div className="space-y-1.5">
          {pageIds.map(id => {
            const page = pageById.get(id);
            return (
              <div key={id} className="flex items-center gap-2.5">
                <div className="flex h-7 w-7 shrink-0 overflow-hidden rounded-full bg-surface-alt">
                  {page?.pictureUrl ? (
                    <img src={page.pictureUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-text-muted">
                      <ImageIcon size={12} />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-text-primary">
                    {page?.name ?? <span className="font-mono text-xs">{id}</span>}
                  </div>
                  {page?.name && <div className="font-mono text-2xs text-text-muted">{id}</div>}
                </div>
                {page?.instagramAccount && (
                  <Camera size={12} className="shrink-0 text-text-muted" aria-label="Has linked Instagram" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
    {!editing && (
      pageIds.length > 0 ? (
        <span className="inline-flex items-center rounded-full bg-success-soft px-2 py-0.5 text-xs font-semibold text-success shrink-0">
          <span className="mr-1 h-1.5 w-1.5 rounded-full bg-success" />
          {pageIds.length} connected
        </span>
      ) : (
        <span className="inline-flex items-center rounded-full bg-surface-alt px-2 py-0.5 text-xs text-text-muted shrink-0">
          Not set
        </span>
      )
    )}
  </div>
);

const InstagramRow: React.FC<{
  editing: boolean;
  value: string | null;
  igById: Map<string, MetaInstagramAccount>;
  onChange: (id: string | null) => void;
}> = ({ editing, value, igById, onChange }) => {
  const account = value ? igById.get(value) : undefined;
  return (
    <div className={`flex items-start gap-3 rounded-xl border p-3.5 transition-colors ${editing ? 'border-border-strong bg-surface-alt' : 'border-border bg-surface'}`}>
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
        <Camera size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-text-secondary mb-1">Instagram Account</div>
        {editing ? (
          <>
            <div className="text-2xs text-text-muted mb-2">
              Optional. When set, ads can cross-post to Instagram using this business account.
              Pick "None" to keep ads Facebook-only.
            </div>
            <IgAccountPicker value={value} onChange={(id) => onChange(id)} />
          </>
        ) : !value ? (
          <span className="italic text-text-muted text-xs">Not connected — Facebook only</span>
        ) : (
          <div className="flex items-center gap-2.5">
            <div className="flex h-7 w-7 shrink-0 overflow-hidden rounded-full bg-surface-alt">
              {account?.profilePictureUrl ? (
                <img src={account.profilePictureUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-text-muted">
                  <Camera size={12} />
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-text-primary">
                {account?.username ? `@${account.username}` : <span className="font-mono text-xs">{value}</span>}
              </div>
              {account?.username && <div className="font-mono text-2xs text-text-muted">{value}</div>}
            </div>
          </div>
        )}
      </div>
      {!editing && (
        value ? (
          <span className="inline-flex items-center rounded-full bg-success-soft px-2 py-0.5 text-xs font-semibold text-success shrink-0">
            <span className="mr-1 h-1.5 w-1.5 rounded-full bg-success" /> Connected
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-surface-alt px-2 py-0.5 text-xs text-text-muted shrink-0">
            Optional
          </span>
        )
      )}
    </div>
  );
};

const BusinessManagerRow: React.FC<{
  editing: boolean;
  value?: string;
  onChange: (v: string) => void;
}> = ({ editing, value, onChange }) => (
  <div className={`flex items-start gap-3 rounded-xl border p-3.5 transition-colors ${editing ? 'border-border-strong bg-surface-alt' : 'border-border bg-surface'}`}>
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
      <Globe size={16} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-xs font-medium text-text-secondary mb-1">Business Manager</div>
      {editing ? (
        <>
          <div className="flex items-start gap-2 rounded-lg border border-info/30 bg-info-soft px-3 py-2 mb-2 text-2xs text-text-secondary">
            <Info size={13} className="mt-0.5 shrink-0 text-info" />
            <div className="space-y-1">
              <div>
                <strong className="text-text-primary">What is Business Manager?</strong>{' '}
                Meta's Business Manager is the central hub that owns your Facebook Pages,
                Instagram accounts and ad accounts under one company profile. You only need its
                ID here if you operate multiple ad accounts under one business — otherwise leave it blank.
              </div>
              <div>
                <strong className="text-text-primary">How to find it:</strong> open{' '}
                <a
                  href="https://business.facebook.com/settings/info"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  business.facebook.com/settings/info <ExternalLink size={10} />
                </a>{' '}
                — your Business Manager ID is shown at the top.
              </div>
            </div>
          </div>
          <Input
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="e.g. 1234567890123456"
            className="h-8 text-xs font-mono"
          />
        </>
      ) : !value ? (
        <span className="italic text-text-muted text-xs">Not set — leave blank if you don't use Business Manager</span>
      ) : (
        <div className="font-mono text-sm text-text-primary break-all">{value}</div>
      )}
    </div>
    {!editing && (
      value ? (
        <span className="inline-flex items-center rounded-full bg-success-soft px-2 py-0.5 text-xs font-semibold text-success shrink-0">
          <span className="mr-1 h-1.5 w-1.5 rounded-full bg-success" /> Connected
        </span>
      ) : (
        <span className="inline-flex items-center rounded-full bg-surface-alt px-2 py-0.5 text-xs text-text-muted shrink-0">
          Optional
        </span>
      )
    )}
  </div>
);

